/* eslint-disable */

/**
 * <dulora-feature-tiles>
 *
 * Wraps the buy-box feature tile grid (.dl-feat) rendered by
 * snippets/dulora-feature-panels.liquid. The grid is tag-driven, so a
 * multi-temperature product carries several Kelvin tags and would render
 * several "LIGHT MOOD" tiles at once. This component reduces the LIGHT MOOD
 * category to the single tile that matches the currently selected variant,
 * and updates it live on variant change.
 *
 * Only tiles marked [data-dl-lightmood] are touched. All other tiles
 * (DIMMABLE, REPLACES, BUILT TO LAST, …) are product-level and left alone.
 *
 * Matching is by Kelvin token: a tile's data-dl-temp ("2700k") and the
 * variant's option values ("2700K" / "2700 Kelvin") are both reduced to their
 * numeric Kelvin and compared. Robust to formatting and admin reconfiguration —
 * nothing here hardcodes the Kelvin list.
 *
 * Blank-guard: if the selected variant yields no Kelvin token, or none of the
 * tiles match it, every LIGHT MOOD tile is shown (today's behaviour). The grid
 * never collapses below what it renders without JS.
 */
if (!customElements.get('dulora-feature-tiles')) {
  class DuloraFeatureTiles extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.dl-feat__item'));
      this.tiles = Array.from(this.querySelectorAll('[data-dl-lightmood]'));

      // Variant-reactive LIGHT MOOD hiding only matters with 2+ light-mood tiles.
      if (this.tiles.length >= 2) {
        this.section = this.closest('.js-product') || document;
        this.onVariantChange = this.onVariantChange.bind(this);
        this.section.addEventListener('on:variant:change', this.onVariantChange);

        // Re-assert correct state on load from the server-stamped current options,
        // in case the first-paint guess and the live picker disagree.
        const current = (this.dataset.dlCurrent || '').split('|');
        this.applySelection(current); // also lays out the grid
      } else {
        // Static tile count — still balance the partial last row.
        this.layout();
      }
    }

    /**
     * Balance the final (partial) row of the 3-up grid based on how many tiles
     * are *visible* (hidden LIGHT MOOD tiles are excluded). Pure CSS :nth-child
     * can't do this because hidden tiles stay in the DOM and skew the count.
     *   count % 3 === 1 -> last tile spans the full row.
     *   count % 3 === 2 -> last two tiles each span half the row.
     *   count % 3 === 0 -> full rows, nothing to do.
     * Runs on connect and after every variant change.
     */
    layout() {
      const items = this.items || Array.from(this.querySelectorAll('.dl-feat__item'));
      items.forEach((t) => t.classList.remove('dl-feat__item--full', 'dl-feat__item--half'));

      const visible = items.filter((t) => !t.hidden);
      const rem = visible.length % 3;
      if (rem === 1) {
        visible[visible.length - 1].classList.add('dl-feat__item--full');
      } else if (rem === 2) {
        visible[visible.length - 1].classList.add('dl-feat__item--half');
        visible[visible.length - 2].classList.add('dl-feat__item--half');
      }
    }

    disconnectedCallback() {
      if (this.section) this.section.removeEventListener('on:variant:change', this.onVariantChange);
    }

    onVariantChange(evt) {
      const variant = evt.detail && evt.detail.variant;
      // Unavailable / unknown combination: don't guess, show everything.
      if (!variant || !Array.isArray(variant.options)) {
        this.showAll();
        return;
      }
      this.applySelection(variant.options);
    }

    /**
     * Reduce a string to its Kelvin token, e.g. "2700K" / "2700 Kelvin" -> "2700".
     * Returns null when the value carries no colour temperature.
     */
    static kelvin(value) {
      if (value == null) return null;
      const m = String(value).toLowerCase().match(/(\d{3,5})\s*k(?:elvin)?\b/);
      return m ? m[1] : null;
    }

    /**
     * Show only the LIGHT MOOD tile whose Kelvin matches one of the selected
     * option values. Falls back to showing all if nothing matches.
     * @param {string[]} optionValues - selected variant option values.
     */
    applySelection(optionValues) {
      const selected = new Set(
        (optionValues || [])
          .map((v) => DuloraFeatureTiles.kelvin(v))
          .filter(Boolean)
      );

      let anyMatch = false;
      if (selected.size) {
        anyMatch = this.tiles.some((t) => selected.has(DuloraFeatureTiles.kelvin(t.dataset.dlTemp)));
      }

      if (!anyMatch) {
        this.showAll();
        return;
      }

      this.tiles.forEach((tile) => {
        const k = DuloraFeatureTiles.kelvin(tile.dataset.dlTemp);
        tile.hidden = !selected.has(k);
      });
      this.layout();
    }

    showAll() {
      this.tiles.forEach((tile) => { tile.hidden = false; });
      this.layout();
    }
  }

  customElements.define('dulora-feature-tiles', DuloraFeatureTiles);
}
